import type { ProductContext } from "./types"

type JsonObject = Record<string, unknown>

// Add-to-cart button selectors across the platforms we target. Tiendanube themes use
// the visible submit's `.js-addtocart` / `.btn-add-to-cart` (its hidden `input[name=
// "add_to_cart"]` only carries the product id, so it is intentionally excluded); the
// rest cover custom sites and Shopify.
export const PURCHASE_SELECTOR = [
  ".js-addtocart", ".btn-add-to-cart",
  "[data-add-to-cart]", "button[name='add']", ".add-to-cart"
].join(", ")

// Seen on real stores: product, product.item, og:product, tiendanube:product. Shopify
// themes mark collection pages product.group, which must not match.
const PRODUCT_OG_TYPE = /(^|[:.])product(\.item)?$/i

export function discoverProduct(adapter?: () => ProductContext | null): ProductContext | null {
  const adapted = adapter?.()
  if (adapted) return normalizeProduct(adapted)

  const { product, count } = pageProduct()
  return discoverJsonLd(product) || discoverOpenGraph(count)
}

export function normalizeProduct(product: ProductContext): ProductContext {
  return {
    ...product,
    externalId: String(product.externalId || product.canonicalUrl),
    name: String(product.name || document.title),
    imageUrl: absoluteUrl(product.imageUrl),
    canonicalUrl: absoluteUrl(product.canonicalUrl || location.href).split("#")[0]
  }
}

// Which Product in the page's JSON-LD is this page about? Listing pages embed one per
// card (31 on one real Tiendanube home), and a home can feature a single product, so
// being present is not enough. Returns the page's product, or null plus how many other
// products were seen, which tells Open Graph it is probably looking at a listing.
function pageProduct(): { product: JsonObject | null; count: number } {
  const products: JsonObject[] = []
  for (const script of document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]')) {
    try {
      products.push(...findProducts(JSON.parse(script.textContent || "null")))
    } catch (_error) {
      // Invalid third-party metadata must never break the merchant page.
    }
  }
  const here = products.find((product) => pointsAtThisPage(productUrl(product)))
  const onlyOneUnaddressed = products.length === 1 && !productUrl(products[0])
  return { product: here || (onlyOneUnaddressed ? products[0] : null), count: products.length }
}

function discoverJsonLd(product: JsonObject | null): ProductContext | null {
  if (!product) return null
  const offers = firstObject(product.offers)
  const canonicalUrl = stringValue(product.url) || canonical() || location.href
  const externalId =
    stringValue(product.sku) || stringValue(product.productID) ||
    stringValue(product["@id"]) || canonicalUrl
  const imageUrl = imageValue(product.image) || meta("property", "og:image")
  if (!imageUrl) return null

  return normalizeProduct({
    externalId,
    name: stringValue(product.name) || document.title,
    imageUrl,
    canonicalUrl,
    description: stringValue(product.description),
    category: stringValue(product.category),
    currency: offers ? stringValue(offers.priceCurrency) : undefined,
    priceCents: offers ? cents(offers.price) : undefined
  })
}

function productUrl(product: JsonObject): string | undefined {
  const id = stringValue(product["@id"])
  return stringValue(product.url) || stringValue(firstObject(product.offers)?.url) ||
    (id && /^https?:/i.test(id) ? id : undefined)
}

// Compares paths only: JSON-LD can name another host for the same page (myshopify.com
// against the custom domain). Suffix matching accepts the same product under a locale
// or collection prefix (/es/products/x, /collections/a/products/x).
function pointsAtThisPage(url?: string): boolean {
  const target = url && pathOf(url)
  if (!target) return false
  return [ location.href, canonical() ].some((page) => {
    const path = page && pathOf(page)
    return Boolean(path) && (path === target || path!.endsWith(target) || target.endsWith(path!))
  })
}

function pathOf(url: string): string {
  try {
    return new URL(url, location.href).pathname.replace(/\/+$/, "").toLowerCase()
  } catch (_error) {
    return ""
  }
}

function discoverOpenGraph(jsonLdProducts: number): ProductContext | null {
  const imageUrl = meta("property", "og:image")
  if (!imageUrl) return null

  if (!looksLikeProductPage(jsonLdProducts)) return null

  const canonicalUrl = canonical() || meta("property", "og:url") || location.href
  return normalizeProduct({
    externalId: meta("property", "product:retailer_item_id") || canonicalUrl,
    name: meta("property", "og:title") || document.title,
    description: meta("property", "og:description"),
    imageUrl,
    canonicalUrl,
    currency: meta("property", "product:price:currency"),
    priceCents: cents(meta("property", "product:price:amount"))
  })
}

// Open Graph alone is a weak signal: home, listing and blog pages carry og:image too.
// So it needs backing, strongest first. A declared og:type is trusted over a purchase
// control, because listing pages often have quick-add buttons.
function looksLikeProductPage(jsonLdProducts: number): boolean {
  if (meta("property", "product:retailer_item_id") || meta("property", "product:price:amount")) return true
  if (document.querySelector("[data-vc-mount]")) return true
  const ogType = meta("property", "og:type")?.trim()
  if (ogType) return PRODUCT_OG_TYPE.test(ogType)
  return jsonLdProducts <= 1 && document.querySelector(PURCHASE_SELECTOR) !== null
}

function findProducts(value: unknown): JsonObject[] {
  if (Array.isArray(value)) return value.flatMap(findProducts)
  if (!isObject(value)) return []
  const type = value["@type"]
  if (type === "Product" || (Array.isArray(type) && type.includes("Product"))) return [ value ]
  return findProducts(value["@graph"])
}

function firstObject(value: unknown): JsonObject | null {
  if (Array.isArray(value)) return value.find(isObject) || null
  return isObject(value) ? value : null
}

function imageValue(value: unknown): string | undefined {
  if (typeof value === "string") return value
  if (Array.isArray(value)) return imageValue(value[0])
  if (isObject(value)) return stringValue(value.url) || stringValue(value.contentUrl)
}

function meta(attribute: "property" | "name", value: string): string | undefined {
  return document.querySelector<HTMLMetaElement>(`meta[${attribute}="${value}"]`)?.content || undefined
}

function canonical(): string | undefined {
  return document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href || undefined
}

function cents(value: unknown): number | undefined {
  if (value == null || value === "") return undefined
  const parsed = Number(String(value).replace(",", "."))
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : undefined
}

// Resolve to an absolute URL, but only for http(s). A poisoned JSON-LD `url` or
// `image` could otherwise carry a `javascript:`/`data:` scheme that later lands in
// an href — so any non-http(s) (or unparseable) value collapses to "" and the
// caller treats it as absent (back-link hidden, image simply not loaded).
function absoluteUrl(value: string): string {
  try {
    const url = new URL(value, location.href)
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : ""
  } catch (_error) {
    return ""
  }
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined
}

function isObject(value: unknown): value is JsonObject {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value)
}
